// The signed entry from the CRM's «پنل» button (docs/staff-access.md section 2): the query the CRM sends and the
// refusals of api/staff-sessions/exchange. Pure, so the page and its tests share it.

export const STAFF_SSO_VERSION = 'staff-v1'

export type StaffSsoParams = {
  version: string
  userId: string
  fullName: string
  admin: string
  access: string
  ts: string
  nonce: string
  sig: string
}

// '*' for a CRM full administrator, else «key:level» entries joined with «,» (empty: the server says why).
const ACCESS_PATTERN = /^(\*|[a-zA-Z]+:(view|manage)(,[a-zA-Z]+:(view|manage))*)?$/

/** The query as the CRM signed it, or undefined when a part is missing or malformed. Values are kept verbatim. */
export const readStaffSsoParams = (searchParams: URLSearchParams): StaffSsoParams | undefined => {
  const read = (name: string) => searchParams.get(name) ?? ''
  const params: StaffSsoParams = {
    version: read('version'),
    userId: read('userId'),
    fullName: read('fullName'),
    admin: read('admin'),
    access: read('access'),
    ts: read('ts'),
    nonce: read('nonce'),
    sig: read('sig'),
  }

  if (
    params.version !== STAFF_SSO_VERSION ||
    !/^\d{1,10}$/.test(params.userId) ||
    !params.fullName.trim() ||
    params.fullName.length > 150 ||
    (params.admin !== '0' && params.admin !== '1') ||
    params.access.length > 2000 ||
    !ACCESS_PATTERN.test(params.access) ||
    !/^\d{9,12}$/.test(params.ts) ||
    !/^[a-zA-Z0-9_-]{32,128}$/.test(params.nonce) ||
    !/^[a-f0-9]{64}$/.test(params.sig)
  ) {
    return undefined
  }
  return params
}

// The page of the panel to open after the entry (`next`, unsigned): only a «زنجیره تأمین» case's request, which the
// CRM's «باز کردن در پنل» asks for (security review 2026-10-30). The CRM checks it the same way
// (Settings_PanelStaffAccess_Entry_Helper::LANDING_PATTERN); it only chooses the page.
const LANDING_PATTERN = /^\/supply-chain\/admin\/crm\/[0-9]{1,12}$/

/** The page to open after the entry, or undefined for the first granted one. */
export const readStaffLandingPath = (searchParams: URLSearchParams): string | undefined => {
  const next = (searchParams.get('next') ?? '').trim()
  return LANDING_PATTERN.test(next) ? next : undefined
}

export const STAFF_SSO_EXPIRED = 'STAFF_SSO_EXPIRED'
const EXCHANGE_WINDOW_MS = 2 * 60 * 1000

type HttpFailure = {response?: {status?: number; data?: {code?: unknown}}}

/** The server answered, but with no staff session: neither a lost connection nor a refusal. */
export class StaffSsoAnswerError extends Error {}

/** The refusal's code: the server's, else one derived from the status or the link's age. */
export const staffSsoErrorCode = (error: unknown, ts?: string, now = Date.now()): string => {
  if (error instanceof StaffSsoAnswerError) return 'UNKNOWN'
  const response = (error as HttpFailure | null | undefined)?.response
  const code = response?.data?.code
  if (typeof code === 'string' && code) return code
  const signedAt = Number(ts || 0) * 1000
  if (signedAt > 0 && now - signedAt > EXCHANGE_WINDOW_MS) return STAFF_SSO_EXPIRED
  if (!response) return 'NETWORK'
  switch (response.status) {
    case 401:
      return 'STAFF_SSO_INVALID'
    case 403:
      return 'STAFF_NO_ACCESS'
    case 409:
      return 'STAFF_SSO_REPLAY'
    case 503:
      return 'STAFF_SSO_NOT_CONFIGURED'
    default:
      return 'UNKNOWN'
  }
}

export type StaffSsoError = {title: string; message: string}

/** What the staff member reads, and what to do next, for each refusal. */
export const staffSsoError = (code: string, brand: string): StaffSsoError => {
  switch (code) {
    case 'STAFF_NO_ACCESS':
      return {
        title: 'دسترسی پنل تعریف نشده است',
        message:
          'برای نقش شما در CRM هیچ بخشی از پنل تعریف نشده است. مدیر CRM می‌تواند آن را در «تنظیمات › دسترسی‌های پنل» تعیین کند.',
      }
    case 'STAFF_SSO_REPLAY':
      return {
        title: 'این لینک قبلاً استفاده شده است',
        message: 'هر ورود از CRM فقط یک بار کار می‌کند. به CRM برگردید و دوباره روی «پنل» بزنید.',
      }
    case STAFF_SSO_EXPIRED:
      return {
        title: 'زمان این ورود گذشته است',
        message: 'ورود از CRM فقط دو دقیقه معتبر است. به CRM برگردید و دوباره روی «پنل» بزنید.',
      }
    case 'STAFF_SSO_NOT_CONFIGURED':
      return {
        title: 'ورود از CRM هنوز راه‌اندازی نشده است',
        message: `ورود کارکنان روی سرور ${brand} تنظیم نشده است. موضوع را به مدیر سامانه اطلاع دهید.`,
      }
    case 'NETWORK':
      return {
        title: 'ارتباط برقرار نشد',
        message: `ارتباط با سرور ${brand} برقرار نشد. اینترنت را بررسی کنید و از CRM دوباره وارد شوید.`,
      }
    case 'STAFF_SSO_INVALID':
    case 'INVALID_LINK':
      return {
        title: 'لینک ورود معتبر نیست',
        message:
          'اطلاعات ورود از CRM ناقص یا نامعتبر است. به CRM برگردید و دوباره روی «پنل» بزنید.',
      }
    default:
      return {
        title: 'ورود از CRM انجام نشد',
        message: 'ورود انجام نشد. به CRM برگردید و دوباره روی «پنل» بزنید.',
      }
  }
}
